import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadFailure, scheduleStaleChunkReload } from '../lib/chunkLoadRecovery';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  message: string;
  isChunkError: boolean;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: '', isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const chunk = isChunkLoadFailure(error);
    return {
      hasError: true,
      isChunkError: chunk,
      message: chunk
        ? 'This page is refreshing to load the latest version (normal right after an update).'
        : (error?.message || 'Unexpected application error.'),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('NET360 UI runtime error:', error, errorInfo);
    }
    if (isChunkLoadFailure(error)) {
      scheduleStaleChunkReload('ErrorBoundary');
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { isChunkError, message } = this.state;
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
          <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-6 shadow-sm">
            <h1 className="mb-2 text-xl text-rose-700">
              {isChunkError ? 'Updating NET360' : 'We hit a temporary issue'}
            </h1>
            <p className="mb-4 text-sm text-slate-600">
              {isChunkError
                ? 'We are loading a fresh copy of the app for you. If nothing changes in a few seconds, tap Reload.'
                : 'Please reload the app and try again. If this keeps happening, contact support.'}
            </p>
            {import.meta.env.DEV && message ? (
              <p className="mb-5 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">{message}</p>
            ) : (
              <div className="mb-5" />
            )}
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex h-10 items-center justify-center rounded-md bg-indigo-600 px-4 text-sm text-white hover:bg-indigo-500"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
